import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import styled, { useTheme } from 'styled-components';
import { NavLink } from 'react-router-dom';
import {
  query,
  collection,
  where,
  orderBy,
  limit,
  getDocs,
  DocumentSnapshot,
  QueryConstraint,
  OrderByDirection,
  QueryDocumentSnapshot,
  getCountFromServer,
  startAfter,
} from 'firebase/firestore';
import { SingleValue } from 'react-select';
import { Grid } from 'react-loader-spinner';
import { buildLookupObject, capitalize } from 'utilities';
import { PUZZLE_DROPDOWN_OPTIONS, PUZZLE_STATUSES, PUZZLE_TYPE_OPTIONS } from 'app-constants';
import { Status, SortOder } from 'shared/enums';
import { StatusLabel } from 'shared/Components/StatusLabel';

import { firestore } from '../../../../../services/Firebase';
import { getAllCollections, getArtists } from '../../../../../services';
import { Button, Select, Splash } from '../../../../../shared/Components';
import { ActionButtons, TableComponent } from '../../../../../shared/Components/Table/Table';
import { strings } from '../../../../../strings';
import { PuzzleStatus } from '../../../../../types';
import { FlexContainer, Image, Text } from '../../../../../shared/Typhography';
import { Option } from '../../../../../shared/Components/Select/Select';
import { NotSubmittedIcon } from '../../../../../assets/PuzzleTable';
import { FilterReducerAction, FilterReducerState } from '../PuzzlesScreen';
import usePremiumPuzzlePrices from '../../CreatePuzzleScreen/hooks/usePremiumPuzzles';

import { DeletePuzzleButton } from './DeletePuzzleButton';
import { RestorePuzzleButton } from './RestorePuzzleButton';
import { AIIcon } from './AIIcon';

type ImageProcessingStates = 'pending' | 'processing' | 'completed' | 'failed';

interface Data {
  index: number;
  firebaseId: string;
  icon: string;
  physicalDistribution: boolean;
  title: string;
  artist: QueryDocumentSnapshot;
  status: string;
  submited: string;
  collectionId: string;
  premiumTier: number;
  collectionIdFirebase: string;
  collectionName: string;
  statusItem: string;
  keywords: string;
  aiGenerated: boolean;
  collections?: string[];
  _meta?: {
    iconState: ImageProcessingStates;
    imageState: ImageProcessingStates;
  };
  createdAt: string;
  type?: 'static' | 'video';
  action: () => void;
  updateOrApprovePuzzleUrl: string;
  __onRowClick__?: () => void;
}

interface PuzzleTableProps {
  filterState: [FilterReducerState, React.Dispatch<FilterReducerAction>];
}

enum SortKeys {
  CREATED_AT = 'createdAt',
  PREMIUM_TIER = 'premiumTier',
}

type SortTypes = {
  key: SortKeys.CREATED_AT | SortKeys.PREMIUM_TIER;
  direction: SortOder.Ascending | SortOder.Descending;
};

const Icon = styled.img`
  width: 64px;
  border-radius: 15px;
`;

const NullText = styled.p`
  color: ${(props) => props.theme.colors.red};
  ${(props) => props.theme.typography.fontBold}
`;

const StyledTableComponent = styled(TableComponent)`
  th,
  td {
    text-align: start;
  }
`;

const filterFieldMapping: { [keys in keyof FilterReducerState]: string } = {
  collection: 'collections',
  artist: 'Publisher',
  aiGenerated: 'aiGenerated',
  status: 'status',
  premiumTier: 'premiumTier',
  type: 'type',
};

// Select component static options.
const trueFalseOptions = [
  {
    value: true,
    label: (
      <FlexContainer margin="0 0 0 10px" alignItems="center">
        <AIIcon value={true} />
      </FlexContainer>
    ),
  },
  {
    value: false,
    label: (
      <FlexContainer margin="0 0 0 10px" alignItems="center">
        <AIIcon value={false} />
      </FlexContainer>
    ),
  },
];

export const PuzzleTable = ({ filterState }: PuzzleTableProps) => {
  const [filters, filterDispatch] = filterState;
  const [artists, setArtists] = React.useState<any>([]);
  const [collections, setCollections] = React.useState<any>([]);
  const [puzzles, setPuzzles] = React.useState<DocumentSnapshot[] | undefined>(undefined);
  const puzzlesLastDoc = React.useRef<DocumentSnapshot | null>(null);
  const [numberOfPuzzles, setNumberOfPuzzles] = React.useState<undefined | number>();
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const { data: artistsData, isLoading: isLoadingArtist } = getArtists();
  const { data: collectionData, isLoading: isLoadingCollectionData } = getAllCollections();
  const { premiumPuzzlePricesDropdownList } = usePremiumPuzzlePrices();

  const puzzleTiers: any = useMemo(() => {
    const data: { value: null | number; label: string }[] = premiumPuzzlePricesDropdownList?.length
      ? [premiumPuzzlePricesDropdownList[0]]
          .concat({ value: -1, label: 'All Premium' })
          .concat(premiumPuzzlePricesDropdownList.slice(1))
      : [];
    return data;
  }, [premiumPuzzlePricesDropdownList]);
  const { colors } = useTheme();

  const [sort, setSort] = useState<SortTypes>({
    key: SortKeys.CREATED_AT,
    direction: SortOder.Descending,
  });

  const [isReady, setReady] = useState<boolean>(false);

  // Artist-Publisher map for puzzle table data.
  const artistsMap = useMemo(
    () =>
      artistsData
        ? (() => {
            setReady(true);
            return buildLookupObject(artistsData.docs, 'id');
          })()
        : {},
    [artistsData, isLoadingArtist],
  );
  const previusArtistDataLength = useRef<number>(0);

  const queryConstraints = useMemo(() => {
    const constraints: Array<QueryConstraint> = [];

    let isAllPremiumFilterActive = false;
    for (const [key, value] of Object.entries(filters)) {
      isAllPremiumFilterActive = key === SortKeys.PREMIUM_TIER && value === -1;
      if (value !== undefined && value !== null && value !== 'ALL') {
        constraints.push(
          where(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            filterFieldMapping[key],
            isAllPremiumFilterActive ? '>' : key === 'collection' ? 'array-contains' : '==',
            isAllPremiumFilterActive ? 0 : value,
          ),
        );

        if (isAllPremiumFilterActive && sort.key === SortKeys.CREATED_AT) {
          constraints.push(orderBy(SortKeys.PREMIUM_TIER, SortOder.Ascending));
        }
      }
    }

    // If status is not selected, search status then "PUZZLE_STATUSES.approved" or "PUZZLE_STATUSES.pending" by default.
    if (!filters.status) {
      constraints.push(
        where('status', 'in', [
          PUZZLE_STATUSES.approved,
          PUZZLE_STATUSES.pending,
          PUZZLE_STATUSES.edited,
        ]),
      );
    }

    return constraints;
  }, [filters]);

  useEffect(() => {
    fetchMore(true);
  }, [
    filters.artist,
    filters.collection,
    filters.aiGenerated,
    filters.status,
    filters.premiumTier,
    filters.type,
    sort,
    isReady,
  ]);

  const fetchMore = useCallback(
    async (reset = false) => {
      // When search by image filter is active, there is no need to fetch any other data
      // until the filter is cleared.
      if (loadingMore || !isReady) {
        return;
      }

      setLoadingMore(true);

      if (reset) {
        setNumberOfPuzzles(
          (
            await getCountFromServer(query(collection(firestore, 'puzzles'), ...queryConstraints))
          ).data().count ?? 0,
        );
      }

      const queryPuzzle = !reset
        ? query(
            collection(firestore, 'puzzles'),
            ...queryConstraints,
            orderBy(sort.key, sort.direction as OrderByDirection),
            startAfter(puzzlesLastDoc.current ?? undefined),
            limit(20),
          )
        : query(
            collection(firestore, 'puzzles'),
            ...queryConstraints,
            orderBy(sort.key, sort.direction as OrderByDirection),
            limit(20),
          );

      const dataP = await getDocs(queryPuzzle);
      const lastPuzzlesIndex = reset ? 0 : puzzles?.length ?? 0;
      const mappedDocs = dataP?.docs?.map((item, index) => {
        const updateOrApprovePuzzleUrl = artistsMap[item.data().Publisher]?.data()
          .requiresInitialApproval
          ? `/first-approval/${artistsMap[item.data().Publisher].id}`
          : item.data().type === 'video'
          ? `/manage-video-puzzle/${item.id}`
          : `/edit-puzzle/${item.id}`;

        return {
          index: lastPuzzlesIndex + 1 + index,
          id: item.id,
          firebaseId: item.id,
          icon: item.data().iconURL,
          title: item.data().name,
          artist: artistsMap[item.data().Publisher],
          statusItem: item.data().status,
          collectionName: item.data().collectionName,
          keywords: item.data().keywords,
          _meta: item.data()._meta,
          collections: item.data().collections,
          aiGenerated: item.data().aiGenerated,
          premiumTier: item.data().premiumTier,
          type: item.data().type ?? 'static',
          updateOrApprovePuzzleUrl: updateOrApprovePuzzleUrl,
          createdAt:
            item
              .data()
              .createdAt?.toDate()
              .toLocaleString('en-us', { month: 'short', year: 'numeric', day: 'numeric' }) || '/',
          status: item?.data()?.status || 'none',
          //Todo: Use enums from DashboardRoutePath. We have some imports problem atm. Need to resolve that first.
          __onRowClick__: () => window.open(updateOrApprovePuzzleUrl),
        };
      });
      setPuzzles((curr: any) => (curr && !reset ? [...curr, ...mappedDocs] : mappedDocs));
      if (dataP?.docs?.length) {
        puzzlesLastDoc.current = dataP.docs[dataP.docs.length - 1];
      } else {
        puzzlesLastDoc.current = null;
      }

      setLoadingMore(false);
    },
    [
      loadingMore,
      setLoadingMore,
      setNumberOfPuzzles,
      setPuzzles,
      queryConstraints,
      sort,
      artistsMap,
      puzzles,
      isReady,
    ],
  );

  const toggleSort = (key: SortKeys.CREATED_AT | SortKeys.PREMIUM_TIER) => {
    setSort((prevState) => ({
      key,
      direction:
        prevState.key === key
          ? prevState.direction === SortOder.Descending
            ? SortOder.Ascending
            : SortOder.Descending
          : SortOder.Descending,
    }));
  };

  const getArtistsData = () => {
    const finalData = artistsData?.docs
      ?.filter((item) => item.data().Name || item.data().username)
      ?.map((item) => ({
        value: item.id,
        label: (
          <FlexContainer gap="1rem" alignItems="center" flexDirection="row">
            <Image
              size="profileImage"
              borderRadiusType="circle"
              src={item.data().profileImageUrl}
            />
            {`${item.data().Name} (${item.data().username})`}
          </FlexContainer>
        ),
        searchValue: `${item.data().Name} ${item.data().username}`, // For simpler custom search functionality.
      }));
    setArtists(finalData);
  };

  useEffect(() => {
    if (artistsData?.docs && previusArtistDataLength.current !== artistsData?.docs?.length) {
      previusArtistDataLength.current = artistsData?.docs?.length;
      getArtistsData();
    }
  }, [artistsData]);

  const getCollectionsData = () => {
    const finalData = collectionData?.docs?.map((item) => ({
      value: item.id,
      label: item.data().name,
    }));
    setCollections(finalData);
  };

  const headers: Column<Data>[] = React.useMemo(
    () => [
      {
        Header: '#',
        accessor: 'index',
        Cell: (cell) => {
          return <FlexContainer width="20px">{cell.row.original.index}</FlexContainer>;
        },
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: strings.puzzleTableHeaderPuzzle,
        accessor: 'icon',
        Cell: (cell) => {
          // Whenever a new image is uploaded it needs to be resized to puzzle and icon size
          // first which can take time.
          // Show loader for images that are still being processed by cloud function.
          const iconState = cell.row.original._meta?.iconState;
          const imageState = cell.row.original._meta?.imageState;

          // States that represent that image is still being processed so loading should be
          // shown instead of thumbnail.
          const processingStates = ['pending', 'processing'];

          if (iconState === 'failed' || imageState === 'failed') {
            return (
              <div
                title="Image processing failed"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '64px',
                  height: '64px',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '15px',
                }}
              >
                <NotSubmittedIcon circleProps={{ fill: 'rgba(0, 0, 0, 0.1)' }} />
              </div>
            );
          }

          if (iconState && imageState) {
            if (processingStates.includes(iconState) && processingStates.includes(imageState)) {
              return (
                // Used Grid instead of Splash element because its styles are too fixed.
                <Grid
                  width={40}
                  height={40}
                  color={colors.purple}
                  wrapperStyle={{ position: 'relative', left: '10px', padding: '14px 0' }}
                />
              );
            }
          }

          return <Icon src={cell.row.original.icon} loading="lazy" />;
        },
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: strings.puzzleTableHeaderTitle,
        accessor: 'title',
        Cell: (cell) => {
          return (
            <Text
              style={{ width: (window.screen.width * 10) / 100 }}
              padding="0 0 0 4px"
              ellipsisOnRow={1}
              title={cell.row.original.title}
            >
              {cell.row.original.title}
            </Text>
          );
        },
        disableSortBy: true,
        width: '15%',
      },
      {
        Header: 'Artist',
        accessor: 'artist',
        Cell: (cell) => {
          const artistName = `${cell.row.original.artist?.data().Name} (${
            cell.row.original.artist?.data().username
          })`;

          return (
            <Text
              style={{ width: 180 }}
              padding="0 4px"
              ellipsisOnRow={1}
              onClick={(e) => e.stopPropagation()}
            >
              {cell.row.original.artist?.exists() ? (
                <a
                  href={`/view-artist/${cell.row.original.artist?.id}`}
                  title={artistName}
                  target="_blank"
                  rel="noreferrer"
                >
                  {artistName}
                </a>
              ) : (
                'not-defined'
              )}
            </Text>
          );
        },
        disableSortBy: true,
        width: '10%',
        maxWidth: 200,
      },

      {
        Header: strings.puzzleTableHeaderStatus,
        accessor: 'status',
        Cell: (cell) => {
          const key = capitalize(cell?.row?.original?.status || 'None');
          return <StatusLabel value={Status[key as keyof typeof Status]} />;
        },
        disableSortBy: true,
        width: '7.5%',
      },
      {
        Header: 'Created date',
        accessor: 'createdAt',
        Cell: (cell) => <b>{cell.row.original.createdAt}</b>,
        width: '10%',
        onHeaderClick: () => toggleSort(SortKeys.CREATED_AT),
      },
      {
        Header: strings.collection,
        accessor: 'collectionIdFirebase',
        Cell: (cell) => {
          return (
            <div style={{ paddingLeft: 5, width: (window.screen.width * 15) / 100 }}>
              {collections && collections?.length > 0 ? (
                cell?.row?.original?.collections?.map((category: string, i) => (
                  <Text
                    key={`collection-${category}-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      filterDispatch({
                        type: 'COLLECTION',
                        value: category,
                      });
                    }}
                    ellipsisOnRow={1}
                  >
                    {collections.find((clc: Option) => clc.value === category)?.label}
                  </Text>
                ))
              ) : (
                <NullText>No Categories</NullText>
              )}
            </div>
          );
        },
        disableSortBy: true,
        width: '20%',
      },
      {
        Header: 'Price',
        accessor: 'premiumTier',
        Cell: (cell) => {
          let tier = cell?.row?.original?.premiumTier;
          if (isNaN(tier)) tier = 0;
          return (
            <FlexContainer margin="0 0 0 10px" alignItems="center">
              <div
                style={{
                  borderRadius: '50%',
                  background: tier === 0 ? '#A3A8AE' : '#585CEA',
                  width: 22,
                  height: 22,
                  textAlign: 'center',
                }}
              >
                <Text size={0.85} top="2px" color="#fff">
                  {String(tier)}
                </Text>
              </div>
            </FlexContainer>
          );
        },
        disableSortBy: false,
        width: '10%',
        onHeaderClick: () => toggleSort(SortKeys.PREMIUM_TIER),
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: (cell) => (
          <Text
            size={0.85}
            top="2px"
            color="black"
            weight="bold"
            style={{ textTransform: 'uppercase' }}
          >
            {cell.row.original.type}
          </Text>
        ),
        disableSortBy: false,
        width: '10%',
      },
      {
        Header: strings.aiGenerated,
        accessor: 'aiGenerated',
        Cell: (cell) => {
          return (
            <FlexContainer
              style={{ paddingLeft: 5 }}
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <AIIcon value={!!cell.row.original.aiGenerated} />
            </FlexContainer>
          );
        },
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: strings.puzzleTableHeaderActions,
        accessor: 'action',
        Cell: (cell) => {
          const status = cell.row.original.statusItem;

          const canDelete =
            status === PUZZLE_STATUSES.approved ||
            status === PUZZLE_STATUSES.edited ||
            status === PUZZLE_STATUSES.pending ||
            status === PUZZLE_STATUSES.rejected ||
            status === PUZZLE_STATUSES.disabled;

          const canEdit = status !== PUZZLE_STATUSES.deleted;

          return (
            <ActionButtons onClick={(e) => e.stopPropagation()}>
              {canDelete ? (
                // TODO #2uabepv
                // onCompleted callback is artificial and should be removed, as
                // PuzzleTable shall subscribe to firebase collection changes and update automatically.
                <DeletePuzzleButton
                  id={cell.row.original.firebaseId}
                  onCompleted={async () => {
                    await fetchMore(true);
                  }}
                  puzzle={cell.row.original}
                />
              ) : (
                <div style={{ width: 100 }} />
              )}
              {cell.row.original.statusItem == PuzzleStatus.PENDING_DELETE && (
                <RestorePuzzleButton id={cell.row.original.firebaseId} />
              )}
              <NavLink to={cell.row.original.updateOrApprovePuzzleUrl}>
                {/* Above route edit-puzzle need to be in brackets ENUM but i got some type error Need to be fixed */}
                <Button disabled={!canEdit} variant="link">
                  {strings.edit}
                </Button>
              </NavLink>
              <NavLink
                to={
                  cell.row.original.artist?.data().requiresInitialApproval &&
                  status !== PUZZLE_STATUSES.deleted
                    ? `/view-artist/${cell.row.original.artist.id}`
                    : `/view-puzzle/${cell.row.original.firebaseId}`
                }
              >
                {/* Above route edit-puzzle need to be in brackets ENUM but i got some type error Need to be fixed */}
                <Button variant="link">View</Button>
              </NavLink>
              <NavLink to={`/revenue-by-puzzle/${cell.row.original.firebaseId}`}>
                <Button variant="link">Revenue</Button>
              </NavLink>
            </ActionButtons>
          );
        },
        disableSortBy: true,
      },
    ],
    [puzzles, artistsMap],
  );

  React.useEffect(() => {
    if (!isLoadingCollectionData) {
      getCollectionsData();
    }
  }, [isLoadingCollectionData]);

  if (!puzzles) {
    return <Splash />;
  }

  return (
    <>
      <FlexContainer gap="1rem" flexWrap="wrap">
        <Text weight="bold">Number of puzzles:</Text> {numberOfPuzzles ?? <Splash width={16} />}
      </FlexContainer>
      <FlexContainer gap="1rem" flexWrap="wrap">
        <Select
          id="puzzle-select-artist"
          label="Artist filter"
          onChange={(e) => filterDispatch({ type: 'ARTIST', value: e?.value.toString() })}
          options={[...artists]}
          value={artists?.find((itm: SingleValue<Option>) => itm?.value == filters.artist)}
          style={{ maxWidth: '250px', minWidth: '200px' }}
          isClearable={true}
          includeCustomSearch
        />

        <Select
          id="puzzle-select-collection"
          label="Collection filter"
          onChange={(e) => filterDispatch({ type: 'COLLECTION', value: e?.value.toString() })}
          options={[...collections]}
          value={collections?.find((itm: SingleValue<Option>) => itm?.value == filters.collection)}
          isClearable={true}
          style={{ maxWidth: '250px', minWidth: '200px' }}
        />

        <Select
          id="puzzle-select-ai"
          label="By AI filter"
          onChange={(e) =>
            filterDispatch({
              type: 'AI_GENERATED',
              value: typeof e?.value === 'boolean' ? e.value : null,
            })
          }
          options={trueFalseOptions}
          value={trueFalseOptions.find(
            (itm: SingleValue<Option>) => itm?.value == filters.aiGenerated,
          )}
          isClearable={true}
          style={{ width: '120px' }}
        />

        <Select
          id="puzzle-select-by-status"
          label="By status"
          onChange={(e) =>
            filterDispatch({
              type: 'STATUS',
              value: e?.value?.toString(),
            })
          }
          options={PUZZLE_DROPDOWN_OPTIONS}
          value={PUZZLE_DROPDOWN_OPTIONS.find(
            (itm: SingleValue<Option>) => itm?.value == filters.status,
          )}
          isClearable={true}
          style={{ width: '120px' }}
        />

        <Select
          id="puzzle-premuim-tier-select"
          label="Price"
          onChange={(e) =>
            filterDispatch({
              type: 'PREMIUMTIER',
              value: typeof e?.value === 'undefined' ? null : e?.value,
            })
          }
          options={puzzleTiers}
          value={puzzleTiers.find(
            (item: SingleValue<Option>) => item?.value == filters.premiumTier,
          )}
          style={{ width: '120px' }}
          isClearable={true}
        />

        <Select
          id="puzzle-select-by-type"
          label="By type"
          onChange={(e) =>
            filterDispatch({
              type: 'TYPE',
              value: e?.value?.toString(),
            })
          }
          options={PUZZLE_TYPE_OPTIONS}
          value={PUZZLE_TYPE_OPTIONS.find((itm: SingleValue<Option>) => itm?.value == filters.type)}
          isClearable={true}
          style={{ width: '120px' }}
        />
      </FlexContainer>

      {!puzzles.length ? (
        <Text size={1.2} textAlign="center">
          {strings.puzzleTableNoMatchesItem}
        </Text>
      ) : (
        <StyledTableComponent
          loadingMore={loadingMore}
          fetchMore={fetchMore}
          columns={headers}
          data={puzzles}
        />
      )}
    </>
  );
};
